import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import api from "../../../components/services/api";

/**
 * แท็บ Employee ของหน้า Edit Location
 * ตั้งพนักงานที่เกี่ยวข้องกับ location นี้ (เพิ่ม/ลบ)
 * พนักงานที่ตั้งไว้จะถูกดึงเป็น SaleOrderCoOwner ตอนสร้าง SO ที่มี Gate In/Out เป็น location นี้
 */
export default function LocationEmployeeTab({ locationId }) {
  const [options, setOptions] = useState([]); // ตัวเลือกพนักงานทั้งหมด (Status = 1)
  const [selected, setSelected] = useState(null);
  const [list, setList] = useState([]); // พนักงานที่ตั้งไว้กับ location นี้
  const [saving, setSaving] = useState(false);

  const fetchEmployees = async () => {
    try {
      const res = await api.get("api/employee/list");
      setOptions((res?.data || []).filter((e) => e?.Status === 1 || e?.Status === true));
    } catch (e) {
      console.error("load employees failed", e);
    }
  };

  const fetchList = async () => {
    if (!locationId) return;
    try {
      const res = await api.post("api/location/employee/list", { LocationId: locationId });
      setList(res?.data || []);
    } catch (e) {
      console.error("load location employees failed", e);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const handleAdd = async () => {
    if (!selected?.EmpId) {
      Swal.fire({ icon: "warning", text: "กรุณาเลือกพนักงานก่อน" });
      return;
    }
    setSaving(true);
    try {
      await api.post("api/location/employee/create", {
        LocationId: locationId,
        EmpId: selected.EmpId,
      });
      setSelected(null);
      await fetchList();
    } catch (err) {
      const msg = err?.response?.data?.message || "เพิ่มพนักงานไม่สำเร็จ (อาจถูกเพิ่มไว้แล้ว)";
      Swal.fire({ icon: "error", text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (row) => {
    Swal.fire({
      title: "ยืนยันการลบ",
      text: `ลบ ${row.EmpName || ""} ${row.EmpLastName || ""} ออกจาก location นี้?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#d33",
    }).then(async (r) => {
      if (!r.isConfirmed) return;
      try {
        await api.post("api/location/employee/delete", {
          LocationEmployeeId: row.LocationEmployeeId,
        });
        await fetchList();
      } catch (e) {
        Swal.fire({ icon: "error", text: "ลบไม่สำเร็จ" });
      }
    });
  };

  // ตัดพนักงานที่ถูกตั้งไว้แล้วออกจากตัวเลือก (ลด noise + กันซ้ำตั้งแต่ฝั่ง UI)
  const linkedIds = new Set(list.map((l) => l.EmpId));
  const availableOptions = options.filter((o) => !linkedIds.has(o.EmpId));

  return (
    <Box sx={{ p: 1 }}>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>
        พนักงานที่เกี่ยวข้องกับ Location นี้
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        พนักงานที่ตั้งไว้จะถูกเพิ่มเป็นผู้เกี่ยวข้อง (Co-Owner) ของ Sale Order โดยอัตโนมัติ
        เมื่อ location นี้ถูกใช้เป็น Gate In หรือ Gate Out ตอนสร้าง Sale Order
      </Typography>

      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}>
        <Autocomplete
          value={selected}
          onChange={(event, newValue) => setSelected(newValue)}
          sx={{ width: 360 }}
          options={availableOptions}
          getOptionLabel={(o) =>
            `${o?.EmpNo || ""} - ${o?.EmpName || ""} ${o?.EmpLastName || ""}`.trim()
          }
          isOptionEqualToValue={(o, v) => o?.EmpId === v?.EmpId}
          renderInput={(params) => (
            <TextField color="success" {...params} label="เลือกพนักงาน" size="small" />
          )}
        />
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={saving}
          startIcon={<AddIcon />}
          sx={{ background: "#80C340", "&:hover": { background: "#6ba832" } }}
        >
          เพิ่ม
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#f5f5f5" }}>
              <TableCell width={60}>#</TableCell>
              <TableCell>รหัสพนักงาน</TableCell>
              <TableCell>ชื่อ - นามสกุล</TableCell>
              <TableCell>อีเมล</TableCell>
              <TableCell width={80} align="center">
                จัดการ
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 3 }}>
                  ยังไม่มีพนักงานที่ตั้งไว้
                </TableCell>
              </TableRow>
            ) : (
              list.map((row, i) => (
                <TableRow key={row.LocationEmployeeId} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{row.EmpNo}</TableCell>
                  <TableCell>
                    {row.EmpName} {row.EmpLastName}
                  </TableCell>
                  <TableCell>{row.Email}</TableCell>
                  <TableCell align="center">
                    <IconButton color="error" size="small" onClick={() => handleDelete(row)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
